import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { 
  FaHome,
  FaSearch,
  FaBuilding,
  FaLaptop,
  FaShieldAlt,
  FaBus,
  FaLeaf,
  FaBook,
  FaFlask,
  FaFutbol,
  FaMusic,
  FaChalkboardTeacher,
  FaPalette,
  FaDumbbell,
  FaTheaterMasks,
  FaSeedling,
  FaWater,
  FaWifi,
  FaVideo,
  FaUserShield,
  FaHeartbeat,
  FaFireExtinguisher,
  FaClock,
  FaArrowRight,
  FaCheckCircle,
  FaMicrochip,
  FaProjectDiagram,
  FaDrum,
  FaPaintBrush,
  FaBasketballBall,
  FaChess,
  FaTableTennis,
  FaVolleyballBall
} from 'react-icons/fa';
import { title as siteTitle } from '../constants/constant';
import Layout from '../components/layout/Layout';
import BreadcrumbImg from '../assets/images/breadcrumb.png';

// Constants Data
const breadcrumbData = {
  home: { text: "Home", path: "/" },
  current: "Facilities",
  title: "Our Facilities",
  image: BreadcrumbImg
};

const statsData = [
  {
    id: 1,
    icon: FaBuilding,
    value: "12+",
    label: "Modern Facilities",
    color: "text-blue-600"
  },
  {
    id: 2,
    icon: FaLaptop,
    value: "40+",
    label: "Computer Systems",
    color: "text-blue-600"
  },
  {
    id: 3,
    icon: FaBook,
    value: "10K+",
    label: "Library Books",
    color: "text-blue-600"
  },
  {
    id: 4,
    icon: FaShieldAlt,
    value: "100%",
    label: "Safe Campus",
    color: "text-blue-600"
  }
];

const filterTabs = [
  { id: "all", label: "All Facilities", icon: FaBuilding, count: 12 },
  { id: "academic", label: "Academic", icon: FaBook, count: 5 },
  { id: "sports", label: "Sports", icon: FaFutbol, count: 1 },
  { id: "arts", label: "Arts", icon: FaPalette, count: 2 },
  { id: "infrastructure", label: "Infrastructure", icon: FaBuilding, count: 4 }
];

const facilitiesData = [
  {
    id: 1,
    name: "Digital Library",
    category: "academic",
    description: "10,000+ books with digital catalog and reading area",
    icon: FaBook,
    gradient: "from-blue-500 to-cyan-500",
    features: ["10K+ Books", "Digital Catalog", "Reading Zone", "E-Books"],
    iconColor: "text-blue-600"
  },
  {
    id: 2,
    name: "Science Laboratories",
    category: "academic",
    description: "Modern labs for Physics, Chemistry & Biology",
    icon: FaFlask,
    gradient: "from-emerald-500 to-teal-500",
    features: ["Physics Lab", "Chemistry Lab", "Biology Lab", "Equipment"],
    iconColor: "text-emerald-600"
  },
  {
    id: 3,
    name: "Computer Lab",
    category: "academic",
    description: "40+ computers with latest software and internet",
    icon: FaLaptop,
    gradient: "from-purple-500 to-pink-500",
    features: ["40+ Systems", "Latest Software", "Internet", "Coding"],
    iconColor: "text-purple-600"
  },
  {
    id: 4,
    name: "Sports Complex",
    category: "sports",
    description: "Indoor and outdoor sports facilities",
    icon: FaFutbol,
    gradient: "from-red-500 to-orange-500",
    features: ["Basketball", "Football", "Cricket", "Badminton"],
    iconColor: "text-red-600"
  },
  {
    id: 5,
    name: "Music & Arts Room",
    category: "arts",
    description: "Creative space for music and fine arts",
    icon: FaMusic,
    gradient: "from-pink-500 to-rose-500",
    features: ["Instruments", "Art Supplies", "Dance Room", "Theatre"],
    iconColor: "text-pink-600"
  },
  {
    id: 6,
    name: "Smart Classrooms",
    category: "academic",
    description: "Digital boards and multimedia learning",
    icon: FaChalkboardTeacher,
    gradient: "from-indigo-500 to-blue-500",
    features: ["Smart Boards", "Projectors", "Audio Systems", "WiFi"],
    iconColor: "text-indigo-600"
  },
  {
    id: 7,
    name: "Activity Rooms",
    category: "arts",
    description: "Special rooms for co-curricular activities",
    icon: FaPalette,
    gradient: "from-amber-500 to-yellow-500",
    features: ["Arts & Crafts", "Drama", "Debate", "Club Activities"],
    iconColor: "text-amber-600"
  },
  {
    id: 8,
    name: "Science Park",
    category: "academic",
    description: "Outdoor learning area with scientific models",
    icon: FaSeedling,
    gradient: "from-green-500 to-emerald-500",
    features: ["Models", "Experiments", "Learning Zone", "Projects"],
    iconColor: "text-green-600"
  },
  {
    id: 9,
    name: "Transport Facility",
    category: "infrastructure",
    description: "GPS enabled buses with trained drivers",
    icon: FaBus,
    gradient: "from-gray-600 to-gray-800",
    features: ["GPS Buses", "Safety", "Routes", "Attendants"],
    iconColor: "text-gray-600"
  },
  {
    id: 10,
    name: "Security Systems",
    category: "infrastructure",
    description: "24/7 CCTV surveillance and security",
    icon: FaShieldAlt,
    gradient: "from-blue-600 to-indigo-600",
    features: ["CCTV", "Security Staff", "Entry Control", "Emergency"],
    iconColor: "text-blue-600"
  },
  {
    id: 11,
    name: "Green Campus",
    category: "infrastructure",
    description: "Eco-friendly environment with gardens",
    icon: FaLeaf,
    gradient: "from-green-600 to-teal-600",
    features: ["Gardens", "Play Area", "Environment", "Rainwater"],
    iconColor: "text-green-600"
  },
  {
    id: 12,
    name: "Assembly Hall",
    category: "infrastructure",
    description: "Large hall for gatherings and events",
    icon: FaBuilding,
    gradient: "from-purple-600 to-pink-600",
    features: ["500 Capacity", "Stage", "Sound System", "Events"],
    iconColor: "text-purple-600"
  }
];

const safetyFeatures = [
  "24/7 CCTV surveillance across campus",
  "Trained security staff and entry control",
  "Medical room with first aid facilities",
  "Fire safety equipment and emergency plans"
];

const techFeatures = [
  "High-speed WiFi across campus",
  "Digital attendance and communication system",
  "Online learning resources and platforms",
  "Digital library with e-books and journals"
];

export default function Facilities() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFacilities = facilitiesData.filter(facility => {
    const matchesFilter = activeFilter === "all" || facility.category === activeFilter;
    const matchesSearch = facility.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         facility.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <Layout>
      <Helmet>
        <title>Facilities | {siteTitle}</title>
        <meta name="description" content="Explore our modern facilities including digital library, science labs, sports complex, smart classrooms, and more at Gyan Rashmi." />
      </Helmet>

      {/* Breadcrumb Section */}
      <div className="relative bg-gray-900">
        <div className="h-40 md:h-48">
          <img 
            alt="Breadcrumb" 
            className="object-cover w-full h-full opacity-60" 
            src={breadcrumbData.image} 
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-2 text-sm text-white">
            <a 
              href={breadcrumbData.home.path} 
              className="flex items-center transition-colors hover:text-gray-200"
            >
              <FaHome className="w-3 h-3 mr-1.5" />
              {breadcrumbData.home.text}
            </a>
            <span className="mx-2">/</span>
            <span className="font-semibold">{breadcrumbData.current}</span>
          </div>
          <h1 className="text-2xl font-bold text-white md:text-3xl">{breadcrumbData.title}</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-3 py-5 mx-auto max-w-7xl md:px-4 md:py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 mb-5 md:grid-cols-4">
          {statsData.map((stat) => (
            <div 
              key={stat.id}
              className="p-3 text-center transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-md"
            >
              <div className="inline-flex items-center justify-center w-8 h-8 mb-1 rounded-full bg-blue-50">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-base font-bold text-gray-900 md:text-lg">{stat.value}</div>
              <div className="text-xs text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search and Filter Bar */}
        <div className="p-3 mb-5 bg-white border border-gray-200 rounded-xl">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {/* Search Input */}
            <div className="relative flex-1">
              <FaSearch className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
              <input 
                placeholder="Search facilities..." 
                className="w-full py-1.5 pl-8 pr-3 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-1.5">
              {filterTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveFilter(tab.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    activeFilter === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <tab.icon className="w-3 h-3" />
                  {tab.label}
                  <span className={`px-1 py-0.5 text-[10px] rounded-full ${
                    activeFilter === tab.id ? 'bg-white/20' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="mb-6">
          <h3 className="mb-3 text-base font-bold text-center text-gray-900">Key Features</h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="p-3 text-center transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
              <div className="inline-flex items-center justify-center w-8 h-8 mb-2 rounded-full bg-blue-50">
                <FaChalkboardTeacher className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-xs font-bold text-gray-900">Smart Classes</div>
              <div className="mt-0.5 text-[10px] text-gray-600">Digital learning</div>
            </div>
            <div className="p-3 text-center transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
              <div className="inline-flex items-center justify-center w-8 h-8 mb-2 rounded-full bg-blue-50">
                <FaVideo className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-xs font-bold text-gray-900">CCTV Security</div>
              <div className="mt-0.5 text-[10px] text-gray-600">24/7 surveillance</div>
            </div>
            <div className="p-3 text-center transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
              <div className="inline-flex items-center justify-center w-8 h-8 mb-2 rounded-full bg-blue-50">
                <FaBus className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-xs font-bold text-gray-900">Transport</div>
              <div className="mt-0.5 text-[10px] text-gray-600">Safe bus service</div>
            </div>
            <div className="p-3 text-center transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
              <div className="inline-flex items-center justify-center w-8 h-8 mb-2 rounded-full bg-blue-50">
                <FaLeaf className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-xs font-bold text-gray-900">Green Campus</div>
              <div className="mt-0.5 text-[10px] text-gray-600">Eco-friendly</div>
            </div>
          </div>
        </div>

        {/* Facilities Grid */}
        <div className="grid gap-4 mb-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredFacilities.map((facility) => (
            <div 
              key={facility.id}
              className="overflow-hidden transition-all bg-white border border-gray-200 shadow-sm rounded-xl hover:shadow-md"
            >
              {/* Header */}
              <div className={`p-4 bg-gradient-to-r ${facility.gradient} text-white`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="p-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
                    <facility.icon className="w-4 h-4" />
                  </div>
                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-white/20 backdrop-blur-sm">
                    {facility.category}
                  </span>
                </div>
                <h3 className="text-sm font-bold">{facility.name}</h3>
              </div>

              {/* Content */}
              <div className="p-4">
                <p className="mb-3 text-xs text-gray-700">{facility.description}</p>
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  {facility.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                      <span className="text-[10px] text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <button className="flex items-center gap-1 text-[10px] font-medium text-blue-600 hover:text-blue-700">
                    Learn More
                    <FaArrowRight className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Safety and Tech Sections */}
        <div className="grid gap-4 mt-6 md:grid-cols-2">
          {/* Safety & Security */}
          <div className="p-4 border border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <FaShieldAlt className="w-4 h-4 text-blue-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Safety & Security</h3>
            </div>
            <ul className="space-y-1.5 text-xs text-gray-700">
              {safetyFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <FaCheckCircle className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Technology Integration */}
          <div className="p-4 border border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-purple-100 rounded-lg">
                <FaLaptop className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Technology Integration</h3>
            </div>
            <ul className="space-y-1.5 text-xs text-gray-700">
              {techFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <FaCheckCircle className="w-3 h-3 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}